<template>
  <div>
    <DxForm id="formularioTicket" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionTicket">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="IdUsuario" editor-type="dxSelectBox" :col-span="4" :visible="isCreatorSelectable"
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...getRemoteSelectBoxDefaultProperties(), ...selectCreatorOptions }">
          <DxLabel :text="$t('tickets.form.creador')" />
          <DxRequiredRule :message="$t('tickets.form.validacion.creador')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="2" :visible="isCreatorSelectable" />
      </DxGroupItem>
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="Asunto" editor-type="dxTextBox" :col-span="4">
          <DxLabel :text="$t('tickets.form.asunto')" />
          <DxRequiredRule :message="$t('tickets.form.validacion.asunto')" />
          <DxStringLengthRule :max="255" :message="$t('tickets.form.validacion.asunto_max_length')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="2" />
        <DxSimpleItem data-field="Tipo" editor-type="dxSelectBox" :col-span="2"
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectTipoOptions, displayExpr: 'translatedText', disabled: tipoReadOnly }">
          <DxLabel :text="$t('tickets.form.tipo')" />
          <DxRequiredRule :message="$t('tickets.form.validacion.tipo')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Subtipo" editor-type="dxSelectBox" :col-span="2" :visible="mostrarCampoSubtipo"
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectSubtipoOptions, displayExpr: 'translatedText' }">
          <DxLabel :text="$t('tickets.form.subtipo')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="2" />
      </DxGroupItem>
      <DxGroupItem :col-count="6" :col-span="6" >
        <DxSimpleItem :col-span="2" :visible="mostrarGridColaborador">
          <DxLabel :text="$t('tickets.form.operacion')" />
          <TicketFormSeleccionOperacion
            v-if="model"
            ref="operacionColaboradorRef"
            v-model="idOperacion"
            variant="colaborador"
            :model="model"
            :tipo-read-only="tipoReadOnly"
            :is-creator-selectable="isCreatorSelectable"
          />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6" :visible="mostrarGridInmobiliaria">
          <DxLabel :text="$t('tickets.form.tramite')" />
          <TicketFormSeleccionOperacion
            v-if="model"
            ref="operacionInmobiliariaRef"
            v-model="idOperacion"
            variant="inmobiliaria"
            :model="model"
            :tipo-read-only="tipoReadOnly"
            :is-creator-selectable="isCreatorSelectable"
          />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6" :visible="esTipoFacturas && (!props.isCreatorSelectable || props.model?.IdUsuario != null)">
          <DxLabel :text="$t('tickets.form.factura')" />
          <TicketFormSeleccionFactura
            v-if="model"
            ref="facturaRef"
            v-model="idFacturaSeleccion"
            :model="model"
            :tipo-read-only="tipoReadOnly"
            :is-creator-selectable="isCreatorSelectable"
          />
        </DxSimpleItem>
        <DxEmptyItem :col-span="4" :visible="esTipoOperaciones" />
      </DxGroupItem>
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="Mensaje.Mensaje" editor-type="dxTextArea" :col-span="6"
          :editor-options="{ height: 160 }">
          <DxLabel :text="$t('tickets.form.mensaje')" />
          <DxRequiredRule :message="$t('tickets.form.validacion.mensaje')" />
        </DxSimpleItem>
        <DxSimpleItem template="templateFileUploader" :col-span="6">
        </DxSimpleItem>
      </DxGroupItem>
      <template #templateFileUploader>
        <MultipleFileUploader :max-files="4" v-model:ficheros="ficheros"
          :textForAddAttachmentLabel="$t('tickets.form.adjuntos')"
          :textForAddAttachmentButton="$t('documento_card.selecciona_fichero')"
          :textForNonDownloadableFiles="$t('tickets.form.texto_adjunto_no_descargable')" mode="button-selector">
        </MultipleFileUploader>
      </template>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxStringLengthRule,
  DxEmptyItem,
  DxSimpleItem,
  DxGroupItem
} from "devextreme-vue/form";
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref, toRef, watch } from 'vue';
import { useIntl } from '@services/i18n';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import type { CreateTicket } from '@/.shared/components/soporte/models/CreateTicket';
import { getSelectBoxDefaultProperties, getRemoteSelectBoxDefaultProperties } from '@/.shared/helpers/grid-helper';
import MultipleFileUploader from '@/.shared/components/file-uploading/MultipleFileUploader.vue';
import TicketFormSeleccionOperacion from '@/.shared/components/soporte/ticket-form/TicketFormSeleccionOperacion.vue';
import TicketFormSeleccionFactura from '@/.shared/components/soporte/ticket-form/TicketFormSeleccionFactura.vue';
import { TipoTicketEnum, type DXLoadOptions } from '@api/api';
import { useAuthorization } from '@/.shared/services/auth/authorization';
import { PermissionsApp } from '@/.shared/services/auth/authorization/authorization-service-impl';
import { EntidadTicketEnum } from './models/EntidadTicketEnum';
import { refEntidadesDesdeFacturaSeleccion, refEntidadesDesdeOperacionSeleccion } from './ticket-form/ticketRefEntidades';
import { useTicketFormTipoSubtipo } from './ticket-form/useTicketFormTipoSubtipo';

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const tipoEmpresa = ref<string | null>(null);
const props = defineProps({
  isCreatorSelectable: {
    type: Boolean,
    default: false,
    required: false
  },
  /** Portal colaboradores: empresa no validada — solo tipo OTROS en el selector. */
  restringirSoloTipoOtros: {
    type: Boolean,
    default: false,
    required: false
  },
  model: {
    type: Object as () => CreateTicket,
    required: false
  }
});

const modelRef = toRef(props, "model");
const {
  selectTipoOptions,
  selectSubtipoOptions,
  mostrarCampoSubtipo,
  actualizarSubtiposParaTipo,
  cargarTiposSelector
} = useTicketFormTipoSubtipo(modelRef);

const onCreadorChanged = (e: any) => {
  const selectedItem = e?.component?.option("selectedItem");
  tipoEmpresa.value = selectedItem?.TipoEmpresaAgente;
};
const ficheros = ref<Ficheros>({});
const tipoReadOnly = ref(false);
const operacionReferencia = computed(() => props.model?.RefEntidadesAsociadas?.
  find(e => e.startsWith(`${EntidadTicketEnum.Operacion}:`))?.split('?tipo=')[0].split(':')[1] ?? '');
const idOperacion = ref<string[]>(operacionReferencia.value ? [operacionReferencia.value] : []);
const facturaReferencia = computed(() =>
  props.model?.RefEntidadesAsociadas?.find(e => e.startsWith(`${EntidadTicketEnum.Factura}:`))?.split(':')[1]?.split('?')[0] ?? '');
const idFacturaSeleccion = ref<string[]>(facturaReferencia.value ? [facturaReferencia.value] : []);
const operacionColaboradorRef = ref<InstanceType<typeof TicketFormSeleccionOperacion>>();
const operacionInmobiliariaRef = ref<InstanceType<typeof TicketFormSeleccionOperacion>>();
const facturaRef = ref<InstanceType<typeof TicketFormSeleccionFactura>>();

const selectCreatorOptions = reactive<any>({
  valueExpr: "IdUsuario",
  displayExpr: "Denominacion",
  onValueChanged: onCreadorChanged,
});

const autho = useAuthorization();

const puedeVerTramitesInmobiliarios = computed(() => {
    return autho.hasPermission(PermissionsApp.ViewSameCompanyTramites) || autho.hasPermission(PermissionsApp.ViewRelatedCompanyBranchTramites) || autho.hasPermission(PermissionsApp.ViewAllTramites);
});

const esTipoOperaciones = computed(() => props.model?.Tipo === TipoTicketEnum.Operaciones);

const esTipoFacturas = computed(() => props.model?.Tipo === TipoTicketEnum.Facturas);

const mostrarGridColaborador = computed(() =>
  (esTipoOperaciones.value && !puedeVerTramitesInmobiliarios.value && props.model?.IdUsuario === null) ||
  (esTipoOperaciones.value && tipoEmpresa.value === "COLABORADORA" && props.model?.IdUsuario !== null)
);

const mostrarGridInmobiliaria = computed(() =>
  (esTipoOperaciones.value && puedeVerTramitesInmobiliarios.value && props.model?.IdUsuario === null) ||
  (esTipoOperaciones.value && tipoEmpresa.value === "INMOBILIARIA" && props.model?.IdUsuario !== null)
);

watch(
  () => props.model?.Tipo,
  async (newTipo) => {
    await nextTick();

    operacionColaboradorRef.value?.clearSelection();
    operacionInmobiliariaRef.value?.clearSelection();
    facturaRef.value?.clearSelection();

    await actualizarSubtiposParaTipo(newTipo);
  }
);

watch(() => props.model?.IdUsuario, () => {
  operacionColaboradorRef.value?.refreshGrid();
  operacionInmobiliariaRef.value?.refreshGrid();
  facturaRef.value?.refreshGrid();
});

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);

  await cargarTiposSelector();

  if (props.restringirSoloTipoOtros && props.model) {
    const ds = (selectTipoOptions.dataSource as any[]) ?? [];
    selectTipoOptions.dataSource = ds.filter((t: any) => t.Denominacion === TipoTicketEnum.Otros);
    props.model.Tipo = TipoTicketEnum.Otros;
    props.model.TipoFijado = true;
    await actualizarSubtiposParaTipo(TipoTicketEnum.Otros);
  } else if (props.model?.Tipo) {
    await actualizarSubtiposParaTipo(props.model.Tipo);
  }

  if (props.model?.TipoFijado || props.restringirSoloTipoOtros) {
    tipoReadOnly.value = true;
  }

  if (props.isCreatorSelectable) {
    const remoteDefaults = getRemoteSelectBoxDefaultProperties();
    const loadAgentes = async (q: DXLoadOptions) =>
      await api.callStandardApi("getAgentesSelector", { showLoading: false }, q);
    const store = api.apiStore({
      key: "IdUsuario",
      load: async (p) => loadAgentes(p as DXLoadOptions),
      byKey: async (key) => {
        const result = await loadAgentes({
          filter: ["IdUsuario", "=", key],
          take: 1
        } as DXLoadOptions);
        const item = result?.data?.[0];
        if (item == null) {
          throw new Error(`Agente no encontrado: ${key}`);
        }
        return item;
      },
      useDefaultSearch: true,
    });
    selectCreatorOptions.dataSource = {
      ...(remoteDefaults.dataSource as object),
      store,
    };
  }

});

async function getFilasSeleccionOperacion(): Promise<any[]> {
  if (mostrarGridColaborador.value) {
    return await operacionColaboradorRef.value?.getSelectedRowsData() ?? [];
  }
  if (mostrarGridInmobiliaria.value) {
    return await operacionInmobiliariaRef.value?.getSelectedRowsData() ?? [];
  }
  return [];
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionTicket");

  if (res.isValid) {
    try {
      await setAdjuntos(props.model!, ficheros.value!);

      let ticket = props.model!;

      if (props.model?.Tipo === TipoTicketEnum.Facturas) {
        const selectedFacturas = await facturaRef.value?.getSelectedRowsData() ?? [];
        if (selectedFacturas.length === 0) {
          toastError(intl.i18n.t('tickets.form.validacion.factura'));
          return false;
        }
        const factura = selectedFacturas[0];
        ticket = { ...props.model!, RefEntidadesAsociadas: refEntidadesDesdeFacturaSeleccion(factura) };
        await api.callStandardApi("createTicket", { showLoading: true }, ticket);
        toastSuccess(intl.i18n.t('tickets.form.creacion_exito'));
        return true;
      }

      const selectedRowsData = await getFilasSeleccionOperacion();

      if (props.model?.Tipo === TipoTicketEnum.Operaciones && selectedRowsData.length > 0) {
        const operacion = selectedRowsData[0];
        ticket = { ...props.model!, RefEntidadesAsociadas: refEntidadesDesdeOperacionSeleccion(operacion) };
      }

      await api.callStandardApi("createTicket", { showLoading: true }, ticket);
      toastSuccess(intl.i18n.t('tickets.form.creacion_exito'));
      return true;
    } catch (e) {
      toastError(intl.i18n.t('tickets.form.creacion_fallo'));
      return false;
    }
  } else {
    toastError(intl.i18n.t('tickets.form.revise_validacion'));
    return false;
  }
}

async function setAdjuntos(modalModel: CreateTicket, ficheros: Ficheros): Promise<void> {
  for (const key of Object.getOwnPropertyNames(ficheros)) {
    const fichero = ficheros[key];
    if (fichero.status === "new" || fichero.status === "modified") {
      const uploadId = await api.callStandardApi("postDocumentoTemp", { showLoading: false }, fichero.fileName!, fichero.file!);
      modalModel.Mensaje.Adjuntos.push({ Nombre: fichero.fileName!, Url: uploadId });
    }
    else if (fichero.status === "unchanged") {
      //modalModel.Mensaje.Adjuntos.push({ Nombre: fichero.fileName!, Url: null });  // Por si alguna vez tenemos multiple subida con edicion : de nada por el hint
    }
  }
}



</script>
